import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Settings, List, Ban, Plus, Trash2, Edit, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingsManager() {
  const [activeSubTab, setActiveSubTab] = useState('settings');
  
  const subTabs = [
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'schedule', label: 'Weekly Schedule', icon: Clock },
    { id: 'calendar', label: 'Calendar View', icon: Calendar },
    { id: 'bookings', label: 'All Bookings', icon: List },
    { id: 'blocked', label: 'Block Time', icon: Ban }
  ];

  return (
    <div>
      <h2 className="text-4xl retro-title mb-6">One-on-One Booking Management</h2>
      
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {subTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`retro-button-small flex items-center space-x-2 ${
                activeSubTab === tab.id ? 'bg-[#E8934D] text-white' : ''
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Sub-tab Content */}
      <div className="cassette-card p-6">
        {activeSubTab === 'settings' && <BookingSettings />}
        {activeSubTab === 'schedule' && <RecurringSchedule />}
        {activeSubTab === 'calendar' && <CalendarView />}
        {activeSubTab === 'bookings' && <BookingsList />}
        {activeSubTab === 'blocked' && <BlockedTimeManager />}
      </div>
    </div>
  );
}

// Booking Settings Component
function BookingSettings() {
  const [settings, setSettings] = useState({
    session_duration_minutes: 45,
    break_duration_minutes: 15,
    booking_increment_minutes: 60
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/booking-settings`, {
        withCredentials: true
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/booking-settings`, null, {
        params: settings,
        withCredentials: true
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-2xl font-bold mb-4 text-[#2C1810]">Booking Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">
            Session Duration (minutes)
          </label>
          <input
            type="number"
            value={settings.session_duration_minutes}
            onChange={(e) => setSettings({ ...settings, session_duration_minutes: parseInt(e.target.value) })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
          />
        </div>
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">
            Break Duration (minutes)
          </label>
          <input
            type="number"
            value={settings.break_duration_minutes}
            onChange={(e) => setSettings({ ...settings, break_duration_minutes: parseInt(e.target.value) })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
          />
        </div>
        <div>
          <label className="block text-[#2C1810] font-bold mb-2">
            Booking Increment (minutes) - Book at top of hour
          </label>
          <input
            type="number"
            value={settings.booking_increment_minutes}
            onChange={(e) => setSettings({ ...settings, booking_increment_minutes: parseInt(e.target.value) })}
            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
            disabled
          />
          <p className="text-sm text-[#2C1810] mt-1 font-semibold">Currently fixed at 60 minutes (top of each hour)</p>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="retro-button w-full sm:w-auto"
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

// Recurring Schedule Component
function RecurringSchedule() {
  const [availability, setAvailability] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '17:00'
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    loadAvailability();
  }, []);

  const loadAvailability = async () => {
    try {
      const response = await axios.get(`${API}/admin/recurring-availability`, {
        withCredentials: true
      });
      setAvailability(response.data);
    } catch (error) {
      console.error('Failed to load availability:', error);
    }
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/admin/recurring-availability`, null, {
        params: formData,
        withCredentials: true
      });
      toast.success('Availability added');
      loadAvailability();
      setShowForm(false);
      setFormData({ day_of_week: 0, start_time: '09:00', end_time: '17:00' });
    } catch (error) {
      toast.error('Failed to add availability');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/recurring-availability/${id}`, {
        withCredentials: true
      });
      toast.success('Availability deleted');
      loadAvailability();
    } catch (error) {
      toast.error('Failed to delete availability');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-2xl font-bold text-[#2C1810]">Weekly Schedule</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="retro-button-small flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Time Slot</span>
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-[#F4E4C1] rounded-lg border-2 border-[#6B4423]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[#2C1810] font-bold mb-2">Day</label>
              <select
                value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              >
                {daysOfWeek.map((day, index) => (
                  <option key={index} value={index}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[#2C1810] font-bold mb-2">Start Time</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              />
            </div>
            <div>
              <label className="block text-[#2C1810] font-bold mb-2">End Time</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleAdd} className="retro-button-small">Add</button>
            <button onClick={() => setShowForm(false)} className="retro-button-small bg-gray-400">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {availability.map((slot) => (
          <div key={slot.id} className="flex justify-between items-center p-4 bg-[#F4E4C1] rounded border border-[#6B4423]">
            <div className="text-[#2C1810]">
              <span className="font-bold">{daysOfWeek[slot.day_of_week]}</span>
              {' '}{slot.start_time} - {slot.end_time}
              {!slot.is_active && <span className="ml-2 text-red-600">(Inactive)</span>}
            </div>
            <button
              onClick={() => handleDelete(slot.id)}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Calendar View Component
function CalendarView() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({ bookings: [], blocked_slots: [] });
  const [view, setView] = useState('month'); // 'month' or 'week'

  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  const loadCalendarData = async () => {
    try {
      const response = await axios.get(`${API}/admin/calendar-data`, {
        params: {
          year: currentDate.getFullYear(),
          month: currentDate.getMonth() + 1
        },
        withCredentials: true
      });
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to load calendar data:', error);
    }
  };

  const changeMonth = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days in month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getBookingsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData.bookings.filter(b => {
      const bookingDate = new Date(b.booking_datetime).toISOString().split('T')[0];
      return bookingDate === dateStr;
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold text-[#2C1810]">Calendar View</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setView('month')}
            className={`retro-button-small ${view === 'month' ? 'bg-[#E8934D] text-white' : ''}`}
          >
            Month
          </button>
          <button
            onClick={() => setView('week')}
            className={`retro-button-small ${view === 'week' ? 'bg-[#E8934D] text-white' : ''}`}
          >
            Week
          </button>
        </div>
      </div>

      {view === 'month' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <button onClick={() => changeMonth(-1)} className="retro-button-small">← Prev</button>
            <h4 className="text-xl font-bold text-[#2C1810]">
              {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </h4>
            <button onClick={() => changeMonth(1)} className="retro-button-small">Next →</button>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center font-bold text-[#2C1810] p-2">
                {day}
              </div>
            ))}
            {getDaysInMonth().map((day, index) => {
              const bookings = getBookingsForDay(day);
              return (
                <div
                  key={index}
                  className={`min-h-[80px] p-2 border border-[#6B4423] rounded ${
                    day ? 'bg-[#F4E4C1]' : 'bg-gray-100'
                  }`}
                >
                  {day && (
                    <>
                      <div className="font-bold text-[#2C1810] mb-1">{day}</div>
                      {bookings.map((booking, i) => (
                        <div
                          key={i}
                          className={`text-xs p-1 mb-1 rounded ${
                            booking.status === 'confirmed' ? 'bg-green-200' :
                            booking.status === 'pending' ? 'bg-yellow-200' :
                            'bg-red-200'
                          }`}
                        >
                          {new Date(booking.booking_datetime).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'week' && (
        <div className="text-center text-[#2C1810] py-8">
          Week view coming soon...
        </div>
      )}
    </div>
  );
}

// Bookings List Component
function BookingsList() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'confirmed', 'cancelled'

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/one-on-one-bookings`, {
        withCredentials: true
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    }
  };

  const updateBooking = async (id, status, zoomLink = null) => {
    try {
      await axios.put(`${API}/admin/one-on-one-bookings/${id}`, null, {
        params: { status, zoom_link: zoomLink },
        withCredentials: true
      });
      toast.success('Booking updated');
      loadBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const cancelBooking = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await axios.delete(`${API}/admin/one-on-one-bookings/${id}`, {
        withCredentials: true
      });
      toast.success('Booking cancelled');
      loadBookings();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const filteredBookings = bookings.filter(b => filter === 'all' || b.status === filter);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-2xl font-bold text-[#2C1810]">All Bookings</h3>
        <div className="flex gap-2">
          {['all', 'pending', 'confirmed', 'cancelled'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`retro-button-small ${filter === f ? 'bg-[#E8934D] text-white' : ''}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {filteredBookings.map((booking) => (
          <div key={booking.id} className="p-4 bg-[#F4E4C1] rounded-lg border-2 border-[#6B4423]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-bold text-[#2C1810]">{booking.user_name}</p>
                <p className="text-[#6B4423]">{booking.user_email}</p>
                {booking.user_phone && <p className="text-[#6B4423]">{booking.user_phone}</p>}
                <p className="text-[#2C1810] mt-2">
                  {new Date(booking.booking_datetime).toLocaleString()}
                </p>
                <p className="text-sm text-[#6B4423]">Duration: {booking.duration_minutes} min</p>
                {booking.notes && (
                  <p className="text-sm text-[#2C1810] mt-2">Notes: {booking.notes}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <span className="text-[#2C1810] font-bold">Status:</span>
                  <span className={`px-3 py-1 rounded ${
                    booking.status === 'confirmed' ? 'bg-green-200 text-green-800' :
                    booking.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                    booking.status === 'cancelled' ? 'bg-red-200 text-red-800' :
                    'bg-gray-200 text-gray-800'
                  }`}>
                    {booking.status}
                  </span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-[#2C1810] font-bold">Payment:</span>
                  <span className={`px-3 py-1 rounded ${
                    booking.payment_status === 'paid' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                  }`}>
                    {booking.payment_status}
                  </span>
                </div>
                {booking.zoom_link && (
                  <a
                    href={booking.zoom_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Zoom Link
                  </a>
                )}
                <div className="flex gap-2 mt-2">
                  {booking.status !== 'confirmed' && (
                    <button
                      onClick={() => updateBooking(booking.id, 'confirmed')}
                      className="retro-button-small flex items-center gap-1"
                    >
                      <CheckCircle className="w-4 h-4" /> Confirm
                    </button>
                  )}
                  {booking.status !== 'cancelled' && (
                    <button
                      onClick={() => cancelBooking(booking.id)}
                      className="retro-button-small bg-red-600 text-white flex items-center gap-1"
                    >
                      <XCircle className="w-4 h-4" /> Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {filteredBookings.length === 0 && (
          <div className="text-center text-[#6B4423] py-8">No bookings found</div>
        )}
      </div>
    </div>
  );
}

// Blocked Time Manager Component
function BlockedTimeManager() {
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    start_datetime: '',
    end_datetime: '',
    reason: ''
  });

  useEffect(() => {
    loadBlockedSlots();
  }, []);

  const loadBlockedSlots = async () => {
    try {
      const response = await axios.get(`${API}/admin/blocked-time-slots`, {
        withCredentials: true
      });
      setBlockedSlots(response.data);
    } catch (error) {
      console.error('Failed to load blocked slots:', error);
    }
  };

  const handleAdd = async () => {
    if (!formData.start_datetime || !formData.end_datetime) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/admin/blocked-time-slots`, null, {
        params: formData,
        withCredentials: true
      });
      toast.success('Time blocked successfully');
      loadBlockedSlots();
      setShowForm(false);
      setFormData({ start_datetime: '', end_datetime: '', reason: '' });
    } catch (error) {
      toast.error('Failed to block time');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/blocked-time-slots/${id}`, {
        withCredentials: true
      });
      toast.success('Blocked time removed');
      loadBlockedSlots();
    } catch (error) {
      toast.error('Failed to remove blocked time');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-2xl font-bold text-[#2C1810]">Block Time</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="retro-button-small flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Block Time</span>
        </button>
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-[#F4E4C1] rounded-lg border-2 border-[#6B4423]">
          <div className="space-y-4">
            <div>
              <label className="block text-[#2C1810] font-bold mb-2">Start Date & Time</label>
              <input
                type="datetime-local"
                value={formData.start_datetime}
                onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              />
            </div>
            <div>
              <label className="block text-[#2C1810] font-bold mb-2">End Date & Time</label>
              <input
                type="datetime-local"
                value={formData.end_datetime}
                onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              />
            </div>
            <div>
              <label className="block text-[#2C1810] font-bold mb-2">Reason (optional)</label>
              <input
                type="text"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="e.g., Vacation, Conference"
                className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-white text-[#2C1810]"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleAdd} className="retro-button-small">Block Time</button>
            <button onClick={() => setShowForm(false)} className="retro-button-small bg-gray-400">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {blockedSlots.map((slot) => (
          <div key={slot.id} className="flex justify-between items-center p-4 bg-[#F4E4C1] rounded border border-[#6B4423]">
            <div className="text-[#2C1810]">
              <p className="font-bold">
                {new Date(slot.start_datetime).toLocaleString()} - {new Date(slot.end_datetime).toLocaleString()}
              </p>
              {slot.reason && <p className="text-sm text-[#6B4423]">Reason: {slot.reason}</p>}
            </div>
            <button
              onClick={() => handleDelete(slot.id)}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}
        {blockedSlots.length === 0 && (
          <div className="text-center text-[#6B4423] py-8">No blocked time slots</div>
        )}
      </div>
    </div>
  );
}
